import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore, Link2, AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import SetupHealthCard from "@/components/SetupHealthCard";
import { useHashHighlight } from "@/lib/highlightRow";

// Two-kind skills library.
// • Assignment skills: linked to an operational Category, name auto-syncs.
// • Additional skills: free-form (languages, licences, systems...).
// Strictly additive, none of this is consumed by rota/leave/billing logic yet.

const EMPTY_ASSIGN = { source_category_id: "", description: "" };
const EMPTY_ADDITIONAL = { name: "", category: "", description: "" };

export default function SkillsPage() {
  const [skills, setSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [tab, setTab] = useState("assignment");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // existing skill (any kind)
  const [createKind, setCreateKind] = useState(null); // "assignment" | "additional" when creating
  const [aForm, setAForm] = useState(EMPTY_ASSIGN);
  const [bForm, setBForm] = useState(EMPTY_ADDITIONAL);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: sk }, { data: cats }] = await Promise.all([
      api.get(`/skills?include_archived=${includeArchived}`),
      api.get("/categories"),
    ]);
    setSkills(sk);
    setCategories(cats);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const assignmentSkills = useMemo(
    () => skills.filter((s) => (s.kind || "additional") === "assignment"),
    [skills]
  );
  const additionalSkills = useMemo(
    () => skills.filter((s) => (s.kind || "additional") !== "assignment"),
    [skills]
  );

  const linkedCategoryIds = useMemo(
    () => new Set(assignmentSkills.map((s) => s.source_category_id).filter(Boolean)),
    [assignmentSkills]
  );

  // deep-link from advisory popovers: #skill={skill_id}.
  // Switches tab to the matching kind, includes archived if needed, then
  // flashes the row.
  useHashHighlight({
    key: "skill", testidPrefix: "skill-row-", deps: [skills, tab],
    onMatch: (id) => {
      const s = skills.find((x) => x.skill_id === id);
      if (!s) return;
      const kind = (s.kind || "additional") === "assignment"
        ? "assignment" : "additional";
      if (tab !== kind) setTab(kind);
      if (s.is_archived && !includeArchived) setIncludeArchived(true);
    },
  });
  const availableCategories = useMemo(
    () => categories.filter(
      (c) => c.is_active !== false &&
             (!linkedCategoryIds.has(c.category_id) ||
              (editing && editing.source_category_id === c.category_id))
    ),
    [categories, linkedCategoryIds, editing]
  );

  // Feb 2026 -- derived work category skills. Show a row for every
  // active work category that does not yet have a real assignment
  // skill row. Manager clicks "Link now" to convert it into a real
  // skill via the existing POST /skills endpoint. No schema change.
  const derivedFromCategories = useMemo(
    () => categories.filter(
      (c) => (c.kind || "work") === "work"
             && c.is_active !== false
             && !linkedCategoryIds.has(c.category_id),
    ),
    [categories, linkedCategoryIds],
  );
  const [linkingCatId, setLinkingCatId] = useState(null);
  const linkDerived = async (cat) => {
    setLinkingCatId(cat.category_id);
    try {
      await api.post("/skills", {
        name: cat.name, kind: "assignment",
        source_category_id: cat.category_id, category: cat.name,
      });
      toast.success(`Linked "${cat.name}" as a skill.`);
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not link");
    } finally { setLinkingCatId(null); }
  };

  const openCreate = (kind) => {
    setEditing(null);
    setCreateKind(kind);
    setAForm(EMPTY_ASSIGN);
    setBForm(EMPTY_ADDITIONAL);
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setCreateKind(null);
    if ((s.kind || "additional") === "assignment") {
      setAForm({
        source_category_id: s.source_category_id || "",
        description: s.description || "",
      });
    } else {
      setBForm({
        name: s.name || "",
        category: s.category || "",
        description: s.description || "",
      });
    }
    setOpen(true);
  };

  const kind = editing ? (editing.kind || "additional") : createKind;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (kind === "assignment") {
        const cat = categories.find((c) => c.category_id === aForm.source_category_id);
        if (!cat) throw new Error("Choose a category");
        const payload = {
          name: cat.name,                   // initial value; future reads sync from category
          kind: "assignment",
          source_category_id: cat.category_id,
          category: cat.name,
          description: aForm.description || null,
        };
        if (editing) {
          await api.patch(`/skills/${editing.skill_id}`, payload);
          toast.success("Assignment skill updated");
        } else {
          await api.post("/skills", payload);
          toast.success("Assignment skill linked");
        }
      } else {
        const payload = {
          name: bForm.name.trim(),
          kind: "additional",
          category: bForm.category || null,
          description: bForm.description || null,
        };
        if (!payload.name) throw new Error("Name is required");
        if (editing) {
          await api.patch(`/skills/${editing.skill_id}`, payload);
          toast.success("Skill updated");
        } else {
          await api.post("/skills", payload);
          toast.success("Skill created");
        }
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async (s) => {
    try {
      await api.patch(`/skills/${s.skill_id}`, { is_archived: !s.is_archived });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow">People</div>
          <h1 className="font-display text-4xl mt-1">Employee skills</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Set up the skills your employees can have. Skills tell NextRota who can do what, so you can flag mis-assignments today and power smarter scheduling suggestions later.
          </p>
          <p className="text-xs text-[#4B5773] mt-1 max-w-2xl">
            Two kinds: skills tied to a type of work (<strong>Assignment skills</strong>),
            and free-form skills like languages, licences or systems (<strong>Additional skills</strong>). Each employee&apos;s own skills are added later on their profile.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[#4B5773]">
            <input type="checkbox" checked={includeArchived} data-testid="skills-include-archived"
              onChange={(e) => setIncludeArchived(e.target.checked)} className="accent-[#2563EB]" />
            Show archived
          </label>
        </div>
      </div>

      <SetupHealthCard page="skills" />

      <div className="mb-4 p-4 rounded-lg bg-[#F6F9FE] border border-[#DBE7FE]"
        data-testid="skills-helper-card">
        <div className="text-sm font-semibold text-[#0B1A36] mb-1">Why add skills?</div>
        <p className="text-xs text-[#1F2A44] leading-relaxed">
          Skills are how NextRota knows who&apos;s qualified for which work. Adding them lets you:
          assign the right people to the right shifts; flag understaffed work
          (e.g. <em>&ldquo;only 2 Spanish-speaking agents available&rdquo;</em>); manage
          certifications and renewals; and unlock future automated scheduling. Adding more
          skills makes everything that comes after work better.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <TabsList className="bg-white border border-slate-200 h-10 p-1" data-testid="skills-tabs">
            <TabsTrigger value="assignment" data-testid="skills-tab-assignment"
              className="data-[state=active]:bg-[#DBE7FE] data-[state=active]:text-[#1D4ED8] rounded-md px-4">
              Assignment skills
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#4B5773]">
                {assignmentSkills.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="additional" data-testid="skills-tab-additional"
              className="data-[state=active]:bg-[#DBE7FE] data-[state=active]:text-[#1D4ED8] rounded-md px-4">
              Additional skills
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#4B5773]">
                {additionalSkills.length}
              </span>
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2 flex-wrap">
            <GenerateSkillsButton onDone={load} />
            <Button onClick={() => openCreate(tab)} data-testid="skills-create-btn"
              className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4 mr-1" />
              {tab === "assignment" ? "Link category" : "New skill"}
            </Button>
          </div>
        </div>

        <TabsContent value="assignment">
          <Card>
            <table className="w-full text-sm" data-testid="assignment-skills-table">
              <thead>
                <tr className="text-left text-[#4B5773]">
                  <th className="py-3 eyebrow font-normal">Skill (from category)</th>
                  <th className="py-3 eyebrow font-normal">Status</th>
                  <th className="py-3 eyebrow font-normal">Description</th>
                  <th className="py-3 eyebrow font-normal text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assignmentSkills.map((s) => (
                  <tr key={s.skill_id} className={s.is_archived ? "opacity-50" : ""}
                      data-testid={`skill-row-${s.skill_id}`}>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-3.5 w-3.5 text-[#2563EB]" />
                        <span className="font-medium text-[#0B1A36]">{s.name}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      {s.linked_category_active === false ? (
                        <Badge tone="amber" testid={`skill-cat-inactive-${s.skill_id}`}>
                          <AlertTriangle className="h-3 w-3 mr-1" />Category inactive
                        </Badge>
                      ) : s.is_archived ? (
                        <Badge tone="slate">Archived</Badge>
                      ) : (
                        <Badge tone="green">Active</Badge>
                      )}
                    </td>
                    <td className="py-3 text-[#4B5773] truncate max-w-md">{s.description || "-"}</td>
                    <td className="py-3 text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)}
                        data-testid={`skill-edit-${s.skill_id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleArchive(s)}
                        data-testid={`skill-archive-${s.skill_id}`}>
                        {s.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </Button>
                    </td>
                  </tr>
                ))}
                {/* Feb 2026 -- derived rows for categories that do not
                    have a real assignment-skill row yet. Keeps the tab
                    from looking empty when categories exist. Clicking
                    "Link now" creates the real row via POST /skills. */}
                {derivedFromCategories.map((c) => (
                  <tr key={`derived_${c.category_id}`}
                      data-testid={`derived-skill-row-${c.category_id}`}>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <Link2 className="h-3.5 w-3.5 text-[#94A3B8]" />
                        <span className="font-medium text-[#0B1A36]">{c.name}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <Badge tone="blue">From category</Badge>
                    </td>
                    <td className="py-3 text-[#4B5773]">Not linked yet</td>
                    <td className="py-3 text-right">
                      <Button variant="ghost" size="sm"
                              onClick={() => linkDerived(c)}
                              disabled={linkingCatId === c.category_id}
                              data-testid={`derived-skill-link-${c.category_id}`}>
                        {linkingCatId === c.category_id ? "..." : "Link now"}
                      </Button>
                    </td>
                  </tr>
                ))}
                {!assignmentSkills.length && !derivedFromCategories.length && (
                  <tr><td colSpan={4} className="py-12 text-center text-[#4B5773]"
                          data-testid="assignment-skills-empty">
                    No work categories yet. Add categories first, then work category skills will show here.
                  </td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="additional">
          <Card>
            <table className="w-full text-sm" data-testid="additional-skills-table">
              <thead>
                <tr className="text-left text-[#4B5773]">
                  <th className="py-3 eyebrow font-normal">Name</th>
                  <th className="py-3 eyebrow font-normal" title="Optional grouping, e.g. 'Language', 'System', 'Certification'.">Group</th>
                  <th className="py-3 eyebrow font-normal">Description</th>
                  <th className="py-3 eyebrow font-normal text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {additionalSkills.map((s) => (
                  <tr key={s.skill_id} className={s.is_archived ? "opacity-50" : ""}
                      data-testid={`skill-row-${s.skill_id}`}>
                    <td className="py-3 font-medium text-[#0B1A36]">{s.name}</td>
                    <td className="py-3 text-[#4B5773]">{s.category || "-"}</td>
                    <td className="py-3 text-[#4B5773] truncate max-w-md">{s.description || "-"}</td>
                    <td className="py-3 text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)}
                        data-testid={`skill-edit-${s.skill_id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleArchive(s)}
                        data-testid={`skill-archive-${s.skill_id}`}>
                        {s.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </Button>
                    </td>
                  </tr>
                ))}
                {!additionalSkills.length && (
                  <tr><td colSpan={4} className="py-12 text-center text-[#4B5773]"
                          data-testid="additional-skills-empty">
                    No additional skills yet. Click &ldquo;New skill&rdquo; to add languages, certifications or systems.
                  </td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="skill-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? (kind === "assignment" ? "Edit assignment skill" : "Edit skill")
                : (kind === "assignment" ? "Link category as skill" : "New skill")}
            </DialogTitle>
            <DialogDescription>
              {kind === "assignment"
                ? "Mirror an operational category as a skill so managers can quickly see which employees are qualified for which work."
                : "Free-form skill such as a language, certification, system or licence."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {kind === "assignment" ? (
              <>
                <div>
                  <Label className="eyebrow">Category</Label>
                  <Select
                    value={aForm.source_category_id}
                    onValueChange={(v) => setAForm({ ...aForm, source_category_id: v })}>
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="skill-category-select">
                      <SelectValue placeholder="Pick an operational category" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.length === 0 && (
                        <div className="px-3 py-2 text-xs text-[#4B5773]">
                          All active categories are already linked.
                        </div>
                      )}
                      {availableCategories.map((c) => (
                        <SelectItem key={c.category_id} value={c.category_id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#4B5773] mt-1.5">
                    Renaming the category will automatically update this skill everywhere.
                  </p>
                </div>
                <div>
                  <Label className="eyebrow">Description (optional)</Label>
                  <Input value={aForm.description} data-testid="skill-description-input"
                    onChange={(e) => setAForm({ ...aForm, description: e.target.value })}
                    placeholder="What does this skill cover for your team?"
                    className="mt-2 h-11 rounded-lg" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="eyebrow">Name</Label>
                  <Input required value={bForm.name} data-testid="skill-name-input"
                    onChange={(e) => setBForm({ ...bForm, name: e.target.value })}
                    placeholder="e.g. Spanish, Salesforce, First Aid"
                    className="mt-2 h-11 rounded-lg" />
                </div>
                <div>
                  <Label className="eyebrow">Group (optional)</Label>
                  <Input value={bForm.category} data-testid="skill-category-input"
                    onChange={(e) => setBForm({ ...bForm, category: e.target.value })}
                    placeholder="Language, System, Certification…"
                    className="mt-2 h-11 rounded-lg" />
                  <p className="text-[11px] text-[#4B5773] mt-1">
                    Optional grouping, e.g. &lsquo;Language&rsquo;, &lsquo;System&rsquo;, &lsquo;Certification&rsquo;, to organise skills.
                  </p>
                </div>
                <div>
                  <Label className="eyebrow">Description (optional)</Label>
                  <Input value={bForm.description} data-testid="skill-description-input"
                    onChange={(e) => setBForm({ ...bForm, description: e.target.value })}
                    className="mt-2 h-11 rounded-lg" />
                </div>
              </>
            )}
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="skill-submit"
                className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                {busy ? "Saving…" : editing ? "Save changes" : (kind === "assignment" ? "Link skill" : "Create skill")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Badge({ tone = "slate", children, testid }) {
  const TONES = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red:   "bg-red-50 text-red-700 border-red-200",
    blue:  "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFD3FB]",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span data-testid={testid}
      className={`inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}

// Skills onboarding: one-click generate from the wizard's
// active template. Same pattern as the other setup pages: only_kinds is
// implicit (the wizard creates skills whenever any rule kind is in scope,
// so we pass `only_kinds: ["assignment"]` which seeds categories + skills
// + assignment rules, the bundle a tenant needs for L3 readiness).

function GenerateSkillsButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  async function run() {
    setBusy(true);
    try {
      const status = await api.get("/setup/wizard-status").catch(() => ({ data: {} }));
      const templateId = status.data?.recommended_template_id || "office_small";
      const { data } = await api.post("/setup/wizard-complete", {
        template_id: templateId,
        industry: status.data?.industry,
        size_band: status.data?.size_band,
        only_kinds: ["assignment"],
        dry_run: false,
      });
      const created = data.counts?.skills || 0;
      if (created > 0) {
        toast.success(`Created ${created} recommended skill${created === 1 ? "" : "s"}. Existing items were kept untouched.`);
      } else {
        toast("No new skills created. Items with matching names already exist.");
      }
      onDone && onDone();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not generate skills");
    } finally { setBusy(false); }
  }
  return (
    <Button onClick={run} disabled={busy} size="sm" variant="outline"
            className="h-10 border-[#BFD3FB] text-[#1D4ED8] hover:bg-[#F0F6FF]"
            data-testid="skills-generate-btn">
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            : <Sparkles className="h-3.5 w-3.5 mr-1" />}
      Generate recommended skills
    </Button>
  );
}

